import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'

import { SealType } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { ConsignmentItemFormValues } from 'features/declaration/consignment-item/models/schema'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import { blankSealType } from 'features/declaration/models/blankItems'
import FormCollapsibleColumn from '../../../../components/p5/FormCollapsibleColumn'
import FormFieldArray from '../../../../components/p5/FormFieldArray'
import FormField from '../../../../components/p5/FormField'
import Input from '../../../../components/p5/Input'
import useDeclarationFieldRequirements from '../../form/components/useDeclarationFieldRequirements'

interface SealsProps {
  transportEquipmentIndex: number
}

function Seals({ transportEquipmentIndex }: Readonly<SealsProps>) {
  const scope = `transportEquipment.${transportEquipmentIndex}.seals` as const
  const { t } = useTranslation()
  const { control } = useFormContext<ConsignmentItemFormValues>()
  const {
    softRemove,
    appendWithSequence,
    visibleFields,
  } = useSoftDeleteFieldArray<ConsignmentItemFormValues, SealType>({
    control,
    name: scope,
  })

  const paths = useMemo(
    () => [
      scope,
      ...visibleFields.map((f) => `${scope}.${f.index}.identifier`),
    ],
    [scope, visibleFields],
  )

  const fieldRequirements = useDeclarationFieldRequirements<ConsignmentItemFormValues>('consignmentItem', paths)

  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  return (
    <>
      <FormCollapsibleColumn
        columnId="declaration-transport-equipment-goods-references"
        headingLabel={t('declaration.p5.seals')}
        fieldRule={fieldRequirements[scope]}
      >
        <div className="transport-entries-grid">
          {
            visibleFields.map((fieldWithIndex) => {
              const sealItem = fieldWithIndex.field
              const innerItemScope = `${scope}.${fieldWithIndex.index}` as const

              return (
                <FormFieldArray
                  key={`sealItem_${sealItem.id}`}
                  titleKey="declaration.p5.sequenceNumber"
                  removeItem={() => softRemove(fieldWithIndex.index)}
                  hidden={sealItem.deleted}
                  sequenceNumber={sealItem.sequenceNumber + 1}
                >
                  <FormField
                    labelKey="declaration.p5.sealIdentifier"
                    fieldRule={fieldRequirements[`${innerItemScope}.identifier`]}
                  >
                    <Input
                      field={`${innerItemScope}.identifier`}
                      labelKey="declaration.p5.sealIdentifier"
                      type="text"
                      placeholder={t('common.fieldPlaceholder')}
                      fieldRule={fieldRequirements[`${innerItemScope}.identifier`]}
                    />
                  </FormField>
                </FormFieldArray>
              )
            })
          }
        </div>
      </FormCollapsibleColumn>
      <div className="row">
        <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
          <button
            type="button"
            className="btn btn-sm btn-primary mt-2"
            onClick={() => appendWithSequence(blankSealType)}
            disabled={isAddForbidden}
          >
            <i className="fa fa-plus me-2" />
            {t('declaration.p5.addNewSeal')}
          </button>
        </div>
      </div>

    </>
  )
}

export default Seals
